import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import {
  createAutosaveController,
  type AutosaveConflict,
  type AutosaveSaveInput,
  type AutosaveSaveResult,
} from './autosave.js';

type UseAutosaveOptions<Value> = {
  initialValue: Value;
  initialRevision: number;
  save(input: AutosaveSaveInput<Value>): Promise<AutosaveSaveResult<Value>>;
  readConflict?(error: unknown): AutosaveConflict<Value> | null;
  debounceMs?: number;
  mode?: 'automatic' | 'manual';
};

export function useAutosave<Value>(options: UseAutosaveOptions<Value>) {
  const lifecycle = useRef(0);
  const controller = useMemo(
    () =>
      createAutosaveController({
        initialValue: options.initialValue,
        initialRevision: options.initialRevision,
        debounceMs: options.debounceMs ?? 700,
        mode: options.mode,
        save: options.save,
        readConflict: options.readConflict,
      }),
    // Aggregate identity/revision deliberately replaces the controller after a server reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options.initialRevision],
  );
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  useEffect(() => {
    const lifecycleToken = ++lifecycle.current;
    const beforeUnload = (event: BeforeUnloadEvent) => controller.handleBeforeUnload(event);
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      queueMicrotask(() => {
        if (lifecycle.current === lifecycleToken) controller.dispose();
      });
    };
  }, [controller]);

  return {
    snapshot,
    getSnapshot: controller.getSnapshot,
    edit: controller.edit,
    flush: controller.flush,
    discardChanges: controller.discardChanges,
    retry: controller.retry,
    acceptServerVersion: controller.acceptServerVersion,
    reapplyLocal: controller.reapplyLocal,
    copyLocal: controller.copyLocal,
  };
}
